/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material'
import {
  faBookmark,
  faClipboardList,
  faFileContract,
  faFileExcel,
  faFilePdf,
  faUser,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { entities } from '../../../../constants/entities'
import useBaseService from '../../../../hooks/services/useBaseService'
import useLoader from '../../../../hooks/useLoader'
import useNotification from '../../../../hooks/useNotification'
import useForm from '../../../../hooks/useForm'
import { setDefaultFieldValues } from '../../../../utils/objectUtils'
import createField from '../../../../utils/factory/createField'
import useRemoteSelectOptions from '../../../../hooks/useRemoteSelectOptions'
import useSelectOptions from '../../../../hooks/useSelectOptions'
import SimpleBody from '../../../../components/forms/SimpleBody'
import SectionHeader from '../../../../components/forms/SectionHeader'
import Button from '../../../../components/Button'
import { organizationPrimaryColors } from '../../../../layouts/PermanentSidebarMUI'
import { useOrgan } from '../../../../hooks/useOrgan'
import { useReport } from '../../../../context/ReportContext'

const reportFields = [
  {
    section: 'Opções do relatório',
    fields: [
      createField({
        id: 'filter.mode',
        label: 'Modo do relatório',
        placeholder: 'Selecione o modo do relatório',
        type: 'select',
        icon: faClipboardList,
        fullWidth: true,
      }),
      createField({
        id: 'filter.customer_id',
        label: 'Cliente Contratante',
        placeholder: 'Selecione o cliente contratante',
        type: 'select',
        icon: faUser,
        isClearable: true,
      }),
      createField({
        id: 'filter.contract_id',
        label: 'Contrato',
        placeholder: 'Selecione o contrato (opcional)',
        type: 'select',
        icon: faFileContract,
        isClearable: true,
      }),
      createField({
        id: 'filter.only_expired',
        label: 'Somente vencidos?',
        placeholder: 'Selecione Sim ou Não',
        type: 'select',
        icon: faFileContract,
      }),
      createField({
        id: 'filter.with_excess',
        label: 'Somente com excedentes?',
        placeholder: 'Selecione Sim ou Não',
        type: 'select',
        icon: faBookmark,
      }),
    ],
  },
]

const DialogExportContracts = ({ open, onClose }) => {
  const navigate = useNavigate()
  const { get: baseServiceGet } = useBaseService(navigate)
  const { showLoader, hideLoader } = useLoader()
  const { showNotification } = useNotification()
  const { setExportId, isExporting } = useReport()
  const { selectedOrgan } = useOrgan()

  const [reportSection] = reportFields

  const { formData, handleChange, setFormData } = useForm(
    setDefaultFieldValues([reportSection]),
    [reportSection]
  )

  // Modo do relatório (detalhado com itens ou simplificado)
  const modeSelectOptions = useSelectOptions({
    fieldId: 'filter.mode',
    formData,
    mapToOption: (item) => ({ value: item.value, label: item.label }),
    setFormData,
    rawOptions: [
      { value: 'detailed', label: 'Detalhado (com itens)' },
      { value: 'simplified', label: 'Simplificado' },
    ],
  })

  // Cliente contratante
  const customerSelectOptions = useRemoteSelectOptions({
    queryKey: ['customers'],
    entityPath: entities.customers.contractants.get,
    baseServiceGet,
    fieldId: 'filter.customer_id',
    formData,
    setFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.alias}`,
    }),
  })

  const customerSelectedRef = useRef(customerSelectOptions.selectedValue?.value)

  // Contrato (opcional, filtrado pelo cliente contratante e organização)
  const contractSelectOptions = useRemoteSelectOptions({
    queryKey: ['contractsByCustomer'],
    entityPath: entities.contracts.get,
    baseServiceGet,
    extraFilters: {
      customer_id: customerSelectOptions.selectedValue?.value,
      organization_id: selectedOrgan?.id,
    },
    defaultSort: { column: 'id', order: 'desc' },
    fieldId: 'filter.contract_id',
    formData,
    setFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    refetchWhen: () => {
      // Mudar quando o cliente selecionado mudar
      if (
        customerSelectedRef.current !==
        customerSelectOptions.selectedValue?.value
      ) {
        customerSelectedRef.current = customerSelectOptions.selectedValue?.value
        contractSelectOptions.reset()
        return true
      }

      return false
    },
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.number} - ${item.name}`,
    }),
  })

  // Somente vencidos?
  const onlyExpiredSelectOptions = useSelectOptions({
    fieldId: 'filter.only_expired',
    formData,
    mapToOption: (item) => ({ value: item.value, label: item.label }),
    setFormData,
    rawOptions: [
      { value: true, label: 'Sim' },
      { value: false, label: 'Não' },
    ],
  })

  // Somente com excedentes?
  const withExcessSelectOptions = useSelectOptions({
    fieldId: 'filter.with_excess',
    formData,
    mapToOption: (item) => ({ value: item.value, label: item.label }),
    setFormData,
    rawOptions: [
      { value: true, label: 'Sim' },
      { value: false, label: 'Não' },
    ],
  })

  useEffect(() => {
    // Por padrão: detalhado, sem somente vencidos e sem somente excedentes
    setFormData((prev) => ({
      filter: {
        ...prev.filter,
        mode: { value: 'detailed', label: 'Detalhado (com itens)' },
        only_expired: { value: false, label: 'Não' },
        with_excess: { value: false, label: 'Não' },
      },
    }))
  }, [])

  const selects = {
    'filter.mode': modeSelectOptions,
    'filter.customer_id': customerSelectOptions,
    'filter.contract_id': contractSelectOptions,
    'filter.only_expired': onlyExpiredSelectOptions,
    'filter.with_excess': withExcessSelectOptions,
  }

  const getSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      if (!selectConfig) return {}

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      }
    },
    [selects]
  )

  const getOptions = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      return selectConfig?.options || []
    },
    [selects]
  )

  const getSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      return selectConfig?.selectedValue || null
    },
    [selects]
  )

  const exportReport = useMutation({
    mutationFn: async (type) => {
      showLoader()

      const detailed = formData.filter.mode?.value !== 'simplified'

      const params = {
        filters: {
          customer_id: formData.filter.customer_id?.value ?? null,
          contract_id: formData.filter.contract_id?.value ?? null,
          only_expired: !!formData.filter.only_expired?.value,
          with_excess: !!formData.filter.with_excess?.value,
          organization_id: selectedOrgan?.id,
        },
      }

      const response = await baseServiceGet(
        entities.reports.contracts.get,
        params,
        { type, detailed }
      )
      return response
    },
    onSuccess: (response) => {
      if (response?.status === 202 || response?.status === 200) {
        const generated_id = response.result?.export_id

        if (generated_id) {
          setExportId(generated_id)
          localStorage.setItem('report_exportId', generated_id.toString())
          showNotification(
            'info',
            'Processamento do relatório iniciado em segundo plano.'
          )
          onClose()
        }
      }
    },
    onError: (error) => {
      console.error('Erro ao exportar relatório de contratos:', {
        status: error.status,
        message: error.message,
      })
      showNotification('error', 'Erro ao iniciar a geração do relatório.')
    },
    onSettled: () => {
      hideLoader()
    },
  })

  const primaryColor = selectedOrgan?.name
    ? organizationPrimaryColors[selectedOrgan.name.toUpperCase()] ||
      organizationPrimaryColors.ALUCOM
    : organizationPrimaryColors.ALUCOM

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      disableEnforceFocus>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: primaryColor,
          color: '#FFFFFF',
        }}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          Relatório de Contratos
        </DialogTitle>
      </Box>
      <DialogContent>
        <div className="container-fluid p-1">
          <SectionHeader section={reportSection.section} />

          <SimpleBody
            fields={reportSection.fields}
            formErrors={{}}
            formData={formData}
            handleFieldChange={handleChange}
            getOptions={getOptions}
            getSelectedValue={getSelectedValue}
            getSelectMeta={getSelectMeta}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" text="Cancelar" />
        <Button
          variant="outlined"
          type="button"
          startIcon={<FontAwesomeIcon icon={faFilePdf} />}
          text="Exportar em PDF"
          disabled={isExporting}
          onClick={() => exportReport.mutate('pdf')}
        />
        <Button
          variant="contained"
          type="button"
          startIcon={<FontAwesomeIcon icon={faFileExcel} />}
          text="Exportar em EXCEL"
          disabled={isExporting}
          onClick={() => exportReport.mutate('excel')}
        />
      </DialogActions>
    </Dialog>
  )
}

export default DialogExportContracts
